import React from "react";
import CampaignDetailsForm, { sanitizeSensitiveFields, transformTargeting } from "../../components/forms/Campaign/CampaignDetailsForm";
import { useOutletContext } from "react-router-dom";
import { App as AntdApp, List, Modal } from "antd";
import { useMutation } from "@apollo/client";
import { UPDATE_CAMPAIGN } from "../../utils/api/campaign";
import { withAsyncErrorToast } from "../../utils/withToast";
import { useCurrentUser } from "../../utils/CurrentUserContext";
import omit from 'lodash.omit';
import { Label, Location, TimeRange, Website } from "../../__generated__/graphql";
import { validateCampaign } from "../../utils/validators";
import { getFloorPriceProperty, getIsMarketplaceCampaign, getWebsiteFloorPrice } from "../../components/forms/Campaign/WebsitesForm";
import { trackEvent } from "../../utils/analytics";

const extractTargeting = (campaign: any) => {
  if (campaign.targeting) {
    const targeting = omit(campaign.targeting, '__typename');
    const timeRange = targeting.timeRange?.map((item: TimeRange) => omit(item, '__typename')) || [];
    
    const location = targeting.location?.reduce((acc: Record<string, any>, value: Location) => {
      const region = value.region as string

      if (!acc[region]) {
        acc[region] = { region, countryCode: value.countryCode}
      }

      if (value.city) {
        if (!acc[region].cities) {
          acc[region].cities = []
        }

        acc[region].cities.push(value.city)
      }
      return acc
    }, {}) || {}
    
    return {targeting: { ...targeting, timeRange, location: Object.values(location) }};
  }

  return {targeting: {}}
}

const Details = () => {
  const { campaign } = useOutletContext<any>();
  const {user} = useCurrentUser();
  const { message, notification } = AntdApp.useApp();
  const [updateCampaign, { loading: saving }] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: ["PublicationMany"],
  });

  const defaultValues = {
    ...campaign,
    ...extractTargeting(campaign),
    rateCard: omit(campaign.rateCard, '__typename'),
    trackingTag: omit(campaign.trackingTag, '__typename'),
    sensitiveCategory: campaign.sensitiveCategory || 'NOT_SENSITIVE',
    labels: campaign.labels.map((label: Label) => label._id),
    campaignPeriod: {
      startDate: campaign.startDate ? new Date(campaign.startDate) : null,
      endDate: campaign.endDate ? new Date(campaign.endDate) : null,
    },
  };
  // TODO: Temporary exemption for specific companies; remove when no longer needed
  const hidePriorityField = ["63c995f2656b6410511ae471"].includes(user?.company?._id);
  const hidePacingField = user?.company?._id === "657033321b972ed3447046ca";
  const handleOnConfirmSubmit = (_, dirtyFields, continueCallback: () => void) => {
    if (getIsMarketplaceCampaign(campaign.type) && dirtyFields.rateCard) {
      const newCampaignBidPrice = dirtyFields.rateCard.price
      const websitesWithHigherFloorPrice: Website[] = campaign.websites.filter(website => {
        const websiteFloorPrice = getWebsiteFloorPrice(user, website, campaign.type)

        return websiteFloorPrice > newCampaignBidPrice
      })

      if (websitesWithHigherFloorPrice.length > 0) {
        return Modal.confirm({
          title: "Are you sure?",
          content: (
            <div>
              <p>
                The following {websitesWithHigherFloorPrice.length} website(s) have a higher floor price than the bid price 
                (<strong>{newCampaignBidPrice}</strong> DKK):
              </p>
              <List
                bordered
                className='mt-3 max-h-96 overflow-auto' 
                dataSource={websitesWithHigherFloorPrice}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.name}
                    />
                    <p><strong>{item[getFloorPriceProperty(campaign.type)]}</strong> DKK</p>
                  </List.Item>
                )}
              />
            </div>
          ),
          onOk: continueCallback,
        });
      }
    }
    continueCallback();
  };
  const handleSubmit = async (values: any, dirtyFields: any) => {
    try {
      validateCampaign(values)

      const { campaignPeriod, targeting, ...campaignDetails } = dirtyFields;
      const sanitizedDetails = sanitizeSensitiveFields(campaignDetails);

      const record = {
        ...sanitizedDetails,
        ...campaignPeriod,
        ...transformTargeting(targeting),
      };

      await withAsyncErrorToast(
        'Update campaign',
        () => updateCampaign({ variables: { id: campaign._id, record } }),
        undefined,
        notification
      );

      message.success('Campaign updated');

      // Track specific field updates
      const updatedFields = Object.keys(dirtyFields);
      trackEvent('campaign_updated', {
        campaignId: campaign._id,
        campaignType: campaign.type,
        updatedFields,
        ...(updatedFields.includes('rateCard') && { bidPriceChanged: true }),
        ...(updatedFields.includes('campaignPeriod') && { datesChanged: true }),
        ...(updatedFields.includes('targeting') && { targetingChanged: true }),
      });
    } catch (error) {
      console.error("Error updating campaign:", error);
      trackEvent('error_occurred', {
        errorType: 'campaign_update_error',
        campaignId: campaign._id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error; // re-throw so Form.tsx skips reset() — a failed save must NOT look saved
    }
  };

  return (
    <CampaignDetailsForm
      className="flex-1 relative bg-white rounded-md py-6 px-6 overflow-y-auto focus:outline-none"
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onConfirmSubmit={handleOnConfirmSubmit}
      submitButtonText="Save"
      accountType={user.company.type}
      campaignStatus={campaign.status}
      everActivated={campaign.everActivated}
      hidePriorityField={hidePriorityField}
      hidePacingField={hidePacingField}
    />
  );
};

export default Details;
